<script>
import axios from "axios";
import Swal from "sweetalert2";

const emptySubject = () => ({ name: "", grade: "" });
const emptyExamination = () => ({
  examType: "",
  examYear: "",
  examNumber: "",
  subjects: [emptySubject()],
});

const examTypeOptions = ["SSCE", "NECO", "GCE", "NABTEB"];
const subjectOptions = [
  "Mathematics",
  "English",
  "Biology",
  "Chemistry",
  "Physics",
  "Government",
  "Economics",
  "Commerce",
  "Literature",
  "Geography",
  "Further Mathematics",
  "Agricultural Science",
];
const gradeOptions = ["A1", "B2", "B3", "C4", "C5", "C6", "D7", "E8", "F9"];

const relationshipOptions = [
  "Parent",
  "Sibling",
  "Spouse",
  "Child",
  "Grandparent",
  "Uncle/Aunt",
  "Cousin",
  "Nephew/Niece",
  "Guardian",
  "Friend",
  "Other",
];

const initialForm = () => ({
  firstName: "",
  otherName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  matriculationNumber: "",
  entrySessionId: "",
  currentSessionId: "",
  programOfferingId: "",
  year: 2,
  semester: 2,
  enrolledAt: new Date().toISOString().split("T")[0],
  importReason: "Verified legacy student onboarding",
  sourceSystem: "Previous school system",
  sourceReference: "",
  dateOfBirth: "",
  gender: "",
  permanentAddress: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "Nigeria",
  },
  nationality: "Nigeria",
  stateOfOrigin: "",
  lga: "",
  placeOfBirth: "",
  maritalStatus: "",
  guardian: { name: "", relationship: "", phone: "", address: "" },
  nextOfKin: { name: "", relationship: "", phone: "", address: "" },
  academicBackground: {
    primary: { name: "", startDate: "", endDate: "" },
    secondary: { name: "", startDate: "", endDate: "" },
  },
  oLevelResultStatus: "awaiting_result",
  examinations: [emptyExamination()],
  jamb: { registrationNumber: "", score: "" },
  selectedPayments: [],
  paymentRecognitionDate: "",
  paymentEvidenceReference: "",
  paymentVerificationConfirmed: false,
  confirmation: "",
});

export default {
  name: "LegacyStudentImportModal",
  emits: ["imported"],
  data() {
    return {
      steps: [
        "Account",
        "Academic Placement",
        "Personal Information",
        "Results & History",
        "Documents",
        "Payments",
        "Review",
      ],
      currentStep: 0,
      form: initialForm(),
      sessions: [],
      programOfferings: [],
      countries: [],
      states: [],
      originLgas: [],
      addressCities: [],
      relationshipOptions,
      examTypeOptions,
      subjectOptions,
      gradeOptions,
      limits: {},
      files: {},
      preview: null,
      previewError: "",
      validatedPayloadSignature: "",
      loadingOptions: false,
      loadingLocations: false,
      loadingOriginLgas: false,
      loadingAddressCities: false,
      locationLoadError: "",
      previewing: false,
      submitting: false,
      showPassword: false,
      formKey: 0,
    };
  },
  computed: {
    selectedOffering() {
      return this.programOfferings.find(
        (offering) => offering._id === this.form.programOfferingId
      );
    },
    availableYears() {
      return this.selectedOffering?.structure?.years || [];
    },
    availableSemesters() {
      return (
        this.availableYears.find(
          (item) => Number(item.year) === Number(this.form.year)
        )?.semesters || [1, 2]
      );
    },
    sortedSessions() {
      return [...this.sessions].sort(
        (a, b) =>
          Number(String(b.sessionYear).slice(0, 4)) -
          Number(String(a.sessionYear).slice(0, 4))
      );
    },
    examYears() {
      const currentYear = new Date().getFullYear();
      return Array.from(
        { length: currentYear - 1980 + 1 },
        (_, index) => currentYear - index
      );
    },
    today() {
      return this.formatLocalDate(new Date());
    },
    maxDateOfBirth() {
      const today = new Date();
      return this.formatLocalDate(
        new Date(today.getFullYear() - 15, today.getMonth(), today.getDate())
      );
    },
    minPrimaryStartDate() {
      return this.addYears(this.form.dateOfBirth, 5);
    },
    minPrimaryEndDate() {
      return this.addYears(this.form.academicBackground.primary.startDate, 1);
    },
    minSecondaryStartDate() {
      return this.form.academicBackground.primary.endDate || null;
    },
    minSecondaryEndDate() {
      return this.addYears(this.form.academicBackground.secondary.startDate, 1);
    },
    importDocumentOptions() {
      const documents = [
        {
          key: "profileImage",
          label: "Passport photograph",
          accept: "image/jpeg,image/png",
        },
        {
          key: "birthCertificate",
          label: "Birth certificate / age declaration",
          accept: "application/pdf",
        },
        {
          key: "jambResult",
          label: "JAMB result",
          accept: "application/pdf",
        },
      ];
      if (this.form.oLevelResultStatus !== "completed") return documents;
      return documents.concat(
        this.form.examinations.map((_, index) => ({
          key: `oLevelResult${index + 1}`,
          label: `O-Level result · Sitting ${index + 1}`,
          accept: "application/pdf",
        }))
      );
    },
    fileTotal() {
      return Object.values(this.files).reduce(
        (total, file) => total + Number(file?.size || 0),
        0
      );
    },
    fileTotalLabel() {
      return `${(this.fileTotal / 1024 / 1024).toFixed(2)} MB / 5 MB`;
    },
    selectedCharges() {
      return (this.preview?.applicableCharges || []).filter((charge) =>
        this.form.selectedPayments.includes(charge.selectionKey)
      );
    },
    canExecute() {
      return (
        this.previewIsCurrent &&
        this.preview?.safeToExecute === true &&
        !this.preview?.conflicts?.length &&
        this.confirmationMatches &&
        !this.submitting
      );
    },
    previewPayloadSignature() {
      const payload = this.payload();
      delete payload.confirmation;
      return JSON.stringify(payload);
    },
    previewIsCurrent() {
      return (
        Boolean(this.preview) &&
        Boolean(this.validatedPayloadSignature) &&
        this.validatedPayloadSignature === this.previewPayloadSignature
      );
    },
    confirmationMatches() {
      const confirmation = String(this.form.confirmation || "")
        .trim()
        .toUpperCase();
      const matriculationNumber = String(this.form.matriculationNumber || "")
        .trim()
        .toUpperCase();
      return Boolean(confirmation) && confirmation === matriculationNumber;
    },
    executeDisabledReason() {
      if (this.previewing) return "Refreshing validation…";
      if (!this.previewIsCurrent)
        return "The form changed. Refreshing the review is required.";
      if (this.preview?.conflicts?.length)
        return "Resolve the validation issues shown above.";
      if (!this.confirmationMatches)
        return "Enter the current matriculation number exactly as shown.";
      return "";
    },
  },
  watch: {
    previewPayloadSignature(newSignature, oldSignature) {
      if (newSignature === oldSignature) return;
      this.validatedPayloadSignature = "";
      if (this.currentStep === this.steps.length - 1) {
        clearTimeout(this._reviewRefreshTimer);
        this._reviewRefreshTimer = setTimeout(() => {
          this.refreshPreview({ quiet: true });
        }, 350);
      }
    },
    "form.matriculationNumber"(newValue, oldValue) {
      if (newValue !== oldValue) this.form.confirmation = "";
    },
    "form.oLevelResultStatus"(status) {
      if (status === "completed" && !this.form.examinations.length) {
        this.form.examinations = [emptyExamination()];
      }
      if (status === "awaiting_result") {
        this.form.examinations = [emptyExamination()];
        delete this.files.oLevelResult1;
        delete this.files.oLevelResult2;
        this.formKey += 1;
      }
      this.clearPreview();
    },
    "form.dateOfBirth"() {
      const primary = this.form.academicBackground.primary;
      if (
        primary.startDate &&
        (!this.minPrimaryStartDate ||
          primary.startDate < this.minPrimaryStartDate)
      ) {
        primary.startDate = "";
        primary.endDate = "";
        this.form.academicBackground.secondary.startDate = "";
        this.form.academicBackground.secondary.endDate = "";
      }
    },
    "form.academicBackground.primary.startDate"() {
      const primary = this.form.academicBackground.primary;
      if (
        primary.endDate &&
        (!this.minPrimaryEndDate || primary.endDate < this.minPrimaryEndDate)
      ) {
        primary.endDate = "";
        this.form.academicBackground.secondary.startDate = "";
        this.form.academicBackground.secondary.endDate = "";
      }
    },
    "form.academicBackground.primary.endDate"() {
      const secondary = this.form.academicBackground.secondary;
      if (
        secondary.startDate &&
        (!this.minSecondaryStartDate ||
          secondary.startDate < this.minSecondaryStartDate)
      ) {
        secondary.startDate = "";
        secondary.endDate = "";
      }
    },
    "form.academicBackground.secondary.startDate"() {
      const secondary = this.form.academicBackground.secondary;
      if (
        secondary.endDate &&
        (!this.minSecondaryEndDate ||
          secondary.endDate < this.minSecondaryEndDate)
      ) {
        secondary.endDate = "";
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this._reviewRefreshTimer);
  },
  methods: {
    formatLocalDate(date) {
      const pad = (value) => String(value).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}`;
    },
    addYears(value, years) {
      if (!value) return null;
      const [year, month, day] = value.split("-").map(Number);
      return new Date(Date.UTC(year + years, month - 1, day))
        .toISOString()
        .split("T")[0];
    },
    availableSubjects(examination, subjectIndex) {
      const selected = examination.subjects.map((subject) => subject.name);
      return this.subjectOptions.filter(
        (subject) =>
          !selected.includes(subject) ||
          subject === examination.subjects[subjectIndex].name
      );
    },
    async open() {
      this.reset();
      this.$refs.openButton?.click();
      await Promise.allSettled([this.fetchOptions(), this.fetchLocations()]);
    },
    reset() {
      this.currentStep = 0;
      this.form = initialForm();
      this.files = {};
      this.preview = null;
      this.previewError = "";
      this.validatedPayloadSignature = "";
      this.showPassword = false;
      this.formKey += 1;
    },
    async fetchOptions() {
      this.loadingOptions = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/utilities/legacy-student-import/options`
        );
        this.sessions = response.data?.data?.sessions || [];
        this.programOfferings = response.data?.data?.programOfferings || [];
        this.limits = response.data?.data?.limits || {};
        const running = this.sortedSessions.find(
          (session) => session.status === "running"
        );
        this.form.currentSessionId =
          running?._id || this.sortedSessions[0]?._id || "";
      } catch (error) {
        Swal.fire(
          "Unable to open importer",
          error.response?.data?.message || "Failed to load import options.",
          "error"
        );
        this.$refs.dismissButton?.click();
      } finally {
        this.loadingOptions = false;
      }
    },
    async fetchLocations() {
      this.loadingLocations = true;
      this.locationLoadError = "";
      try {
        const [countriesResponse, statesResponse] = await Promise.all([
          axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/applications/reference/countries`,
            { withCredentials: false, headers: {} }
          ),
          fetch("https://nga-states-lga.onrender.com/fetch", {
            method: "GET",
            mode: "cors",
            credentials: "omit",
          }),
        ]);

        if (!statesResponse.ok) throw new Error("Failed to load states");

        this.countries = (countriesResponse.data?.data?.countries || [])
          .map((country) => country.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        this.states = (await statesResponse.json())
          .map((state) =>
            typeof state === "string" ? state : state?.name || state?.state
          )
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        await Promise.allSettled([
          this.fetchOriginLgas(),
          this.fetchAddressCities(),
        ]);
      } catch (error) {
        this.locationLoadError =
          "Location lists could not be loaded. Close and reopen the importer to retry.";
      } finally {
        this.loadingLocations = false;
      }
    },
    async fetchNigerianLocalities(state) {
      if (!state) return [];
      const response = await fetch(
        `https://nga-states-lga.onrender.com/?state=${encodeURIComponent(
          state
        )}`,
        { method: "GET", mode: "cors", credentials: "omit" }
      );
      if (!response.ok) throw new Error("Failed to load localities");
      return (await response.json())
        .map((item) =>
          typeof item === "string" ? item : item?.name || item?.lga
        )
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    },
    async fetchOriginLgas({ clearSelection = false } = {}) {
      if (clearSelection) this.form.lga = "";
      this.originLgas = [];
      if (this.form.nationality !== "Nigeria" || !this.form.stateOfOrigin)
        return;
      this.loadingOriginLgas = true;
      try {
        this.originLgas = await this.fetchNigerianLocalities(
          this.form.stateOfOrigin
        );
      } catch (error) {
        this.locationLoadError = "Some location lists could not be loaded.";
      } finally {
        this.loadingOriginLgas = false;
      }
    },
    async fetchAddressCities({ clearSelection = false } = {}) {
      if (clearSelection) this.form.permanentAddress.city = "";
      this.addressCities = [];
      if (
        this.form.permanentAddress.country !== "Nigeria" ||
        !this.form.permanentAddress.state
      )
        return;
      this.loadingAddressCities = true;
      try {
        this.addressCities = await this.fetchNigerianLocalities(
          this.form.permanentAddress.state
        );
      } catch (error) {
        this.locationLoadError = "Some location lists could not be loaded.";
      } finally {
        this.loadingAddressCities = false;
      }
    },
    onNationalityChange() {
      this.form.stateOfOrigin = "";
      this.form.lga = "";
      this.form.placeOfBirth = "";
      this.originLgas = [];
      this.clearPreview();
    },
    onAddressCountryChange() {
      this.form.permanentAddress.state = "";
      this.form.permanentAddress.city = "";
      this.addressCities = [];
      this.clearPreview();
    },
    offeringLabel(offering) {
      return [
        offering.structure?.type?.description || offering.structure?.type?.type,
        offering.structure?.mode?.description || offering.structure?.mode?.mode,
        offering.program?.name,
      ]
        .filter(Boolean)
        .join(" · ");
    },
    sessionLabel(session) {
      return `${session.sessionYear}${
        session.status === "running" ? " · Running" : ""
      }`;
    },
    onOfferingChange() {
      const firstYear = this.availableYears[0];
      if (
        !this.availableYears.some(
          (item) => Number(item.year) === Number(this.form.year)
        )
      ) {
        this.form.year = Number(firstYear?.year || 1);
      }
      this.onYearChange();
      this.clearPreview();
    },
    onYearChange() {
      if (
        !this.availableSemesters
          .map(Number)
          .includes(Number(this.form.semester))
      ) {
        this.form.semester = Number(this.availableSemesters[0] || 1);
      }
      this.clearPreview();
    },
    clearPreview() {
      this.validatedPayloadSignature = "";
    },
    addExamination() {
      if (this.form.oLevelResultStatus !== "completed") return;
      if (this.form.examinations.length < 2)
        this.form.examinations.push(emptyExamination());
    },
    removeExamination(index) {
      if (this.form.examinations.length > 1) {
        this.form.examinations.splice(index, 1);
        delete this.files.oLevelResult2;
        this.formKey += 1;
      }
    },
    addSubject(examination) {
      if (this.form.oLevelResultStatus !== "completed") return;
      if (examination.subjects.length < 11)
        examination.subjects.push(emptySubject());
    },
    removeSubject(examination, index) {
      if (examination.subjects.length > 1)
        examination.subjects.splice(index, 1);
    },
    selectFile(field, event) {
      const file = event.target.files?.[0];
      if (!file) {
        delete this.files[field];
        return;
      }
      const isImage = field === "profileImage";
      const validType = isImage
        ? ["image/jpeg", "image/jpg", "image/png"].includes(file.type)
        : file.type === "application/pdf";
      if (!validType || file.size > 2 * 1024 * 1024) {
        event.target.value = "";
        Swal.fire(
          "Invalid file",
          `${
            isImage ? "Use JPEG or PNG" : "Use PDF"
          } files no larger than 2 MB.`,
          "warning"
        );
        return;
      }
      this.files[field] = file;
      if (this.fileTotal > 5 * 1024 * 1024) {
        delete this.files[field];
        event.target.value = "";
        Swal.fire(
          "Document limit exceeded",
          "Combined application documents must not exceed 5 MB.",
          "warning"
        );
      }
    },
    fileName(field) {
      return this.files[field]?.name || "No file selected";
    },
    payload() {
      return {
        ...this.form,
        documentMetadata: Object.fromEntries(
          Object.entries(this.files).map(([key, file]) => [key, file.name])
        ),
      };
    },
    async refreshPreview({ quiet = false } = {}) {
      this.previewing = true;
      this.previewError = "";
      try {
        const requestPayload = this.payload();
        const requestSignature = this.createPayloadSignature(requestPayload);
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/utilities/legacy-student-import/preview`,
          requestPayload
        );
        const priorSelections = new Set(this.form.selectedPayments);
        this.preview = response.data?.data || null;
        this.form.selectedPayments = (this.preview?.applicableCharges || [])
          .map((charge) => charge.selectionKey)
          .filter((key) => priorSelections.has(key));
        await this.$nextTick();
        this.validatedPayloadSignature =
          requestSignature === this.previewPayloadSignature
            ? requestSignature
            : "";

        if (!this.validatedPayloadSignature) {
          return this.refreshPreview({ quiet });
        }
        return true;
      } catch (error) {
        this.preview = null;
        this.validatedPayloadSignature = "";
        this.previewError =
          error.response?.data?.message || "Unable to validate this import.";
        if (!quiet) {
          Swal.fire("Validation failed", this.previewError, "error");
        }
        return false;
      } finally {
        this.previewing = false;
      }
    },
    createPayloadSignature(payload = this.payload()) {
      const comparable = { ...payload };
      delete comparable.confirmation;
      return JSON.stringify(comparable);
    },
    async nextStep() {
      if (this.currentStep === 4 || this.currentStep === 5) {
        const valid = await this.refreshPreview();
        if (!valid) return;
      }
      if (this.currentStep < this.steps.length - 1) this.currentStep += 1;
    },
    previousStep() {
      if (this.currentStep > 0) this.currentStep -= 1;
    },
    togglePayment(selectionKey) {
      if (this.form.selectedPayments.includes(selectionKey)) {
        this.form.selectedPayments = this.form.selectedPayments.filter(
          (key) => key !== selectionKey
        );
      } else {
        this.form.selectedPayments.push(selectionKey);
      }
    },
    money(value) {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
      }).format(Number(value || 0));
    },
    async executeImport() {
      const valid = await this.refreshPreview();
      if (!valid || !this.canExecute) {
        Swal.fire(
          "Import not ready",
          "Resolve validation issues and confirm the matriculation number.",
          "warning"
        );
        return;
      }
      const confirmation = await Swal.fire({
        title: "Create legacy student?",
        text: `This will create ${this.form.matriculationNumber} and the selected historical records.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Create student",
        confirmButtonColor: "#198754",
      });
      if (!confirmation.isConfirmed) return;

      this.submitting = true;
      try {
        const temporaryPassword = this.form.password;
        const data = new FormData();
        data.append("payload", JSON.stringify(this.payload()));
        Object.entries(this.files).forEach(([field, file]) =>
          data.append(field, file)
        );
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/utilities/legacy-student-import/execute`,
          data,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        const summary = response.data?.data?.summary || {};
        this.$refs.dismissButton?.click();
        this.$emit("imported", summary);
        await Swal.fire({
          title: "Legacy student created",
          icon: "success",
          html: `<div class="text-start">
            <p class="mb-2"><strong>Matriculation number:</strong> ${this.escapeHtml(
              summary.matriculationNumber
            )}</p>
            <p class="mb-2"><strong>Application number:</strong> ${this.escapeHtml(
              summary.applicationNumber
            )}</p>
            <p class="mb-1"><strong>Temporary password:</strong></p>
            <code class="d-block border rounded p-2 bg-light">${this.escapeHtml(
              temporaryPassword
            )}</code>
            <p class="small text-danger mt-2 mb-0">Copy this password now. It is not stored in utility history.</p>
          </div>`,
        });
        this.reset();
      } catch (error) {
        Swal.fire(
          "Import failed",
          error.response?.data?.message || "The student could not be imported.",
          "error"
        );
      } finally {
        this.submitting = false;
      }
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
  },
};
</script>

<template>
  <button
    ref="openButton"
    type="button"
    class="d-none"
    data-bs-toggle="modal"
    data-bs-target="#legacyStudentImportModal"
  ></button>

  <div
    id="legacyStudentImportModal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="legacyStudentImportModalLabel"
    aria-hidden="true"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 id="legacyStudentImportModalLabel" class="modal-title fw-bold">
              Import Legacy Student
            </h5>
            <p class="small text-muted mb-0">
              Create one verified historical student record. Unknown information
              should remain empty.
            </p>
          </div>
          <button
            ref="dismissButton"
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            :disabled="submitting"
          ></button>
        </div>

        <div class="modal-body p-0">
          <div v-if="loadingOptions" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <div class="text-muted mt-2">Loading import options…</div>
          </div>

          <template v-else>
            <div class="legacy-stepper border-bottom px-3 px-lg-4 py-3">
              <button
                v-for="(step, index) in steps"
                :key="step"
                type="button"
                class="legacy-step"
                :class="{
                  active: currentStep === index,
                  complete: currentStep > index,
                }"
                @click="index < currentStep && (currentStep = index)"
              >
                <span>{{ currentStep > index ? "✓" : index + 1 }}</span>
                <small>{{ step }}</small>
              </button>
            </div>

            <div class="p-3 p-lg-4">
              <section v-if="currentStep === 0">
                <h6 class="section-title">Identity and account</h6>
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">First name *</label>
                    <input v-model.trim="form.firstName" class="form-control" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Other name</label>
                    <input v-model.trim="form.otherName" class="form-control" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Last name *</label>
                    <input v-model.trim="form.lastName" class="form-control" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Email *</label>
                    <input
                      v-model.trim="form.email"
                      type="email"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Phone *</label>
                    <input
                      v-model.trim="form.phone"
                      type="tel"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Temporary password *</label>
                    <div class="input-group">
                      <input
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        class="form-control"
                        autocomplete="new-password"
                      />
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        @click="showPassword = !showPassword"
                      >
                        <i
                          :class="
                            showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'
                          "
                        ></i>
                      </button>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label"
                      >Confirm temporary password *</label
                    >
                    <input
                      v-model="form.confirmPassword"
                      :type="showPassword ? 'text' : 'password'"
                      class="form-control"
                      autocomplete="new-password"
                    />
                  </div>
                </div>
              </section>

              <section v-else-if="currentStep === 1">
                <h6 class="section-title">Academic placement</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Matriculation number *</label>
                    <input
                      v-model.trim="form.matriculationNumber"
                      class="form-control text-uppercase"
                      placeholder="F/ND/24010004"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Programme offering *</label>
                    <select
                      v-model="form.programOfferingId"
                      class="form-select"
                      @change="onOfferingChange"
                    >
                      <option value="">Select programme offering</option>
                      <option
                        v-for="offering in programOfferings"
                        :key="offering._id"
                        :value="offering._id"
                      >
                        {{ offeringLabel(offering) }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Entry session *</label>
                    <select
                      v-model="form.entrySessionId"
                      class="form-select"
                      @change="clearPreview"
                    >
                      <option value="">Select entry session</option>
                      <option
                        v-for="session in sortedSessions"
                        :key="session._id"
                        :value="session._id"
                      >
                        {{ sessionLabel(session) }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Current session *</label>
                    <select
                      v-model="form.currentSessionId"
                      class="form-select"
                      @change="clearPreview"
                    >
                      <option value="">Select current session</option>
                      <option
                        v-for="session in sortedSessions"
                        :key="session._id"
                        :value="session._id"
                      >
                        {{ sessionLabel(session) }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Current year *</label>
                    <select
                      v-model.number="form.year"
                      class="form-select"
                      @change="onYearChange"
                    >
                      <option
                        v-for="item in availableYears"
                        :key="item.year"
                        :value="Number(item.year)"
                      >
                        Year {{ item.year }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Current semester *</label>
                    <select
                      v-model.number="form.semester"
                      class="form-select"
                      @change="clearPreview"
                    >
                      <option
                        v-for="semester in availableSemesters"
                        :key="semester"
                        :value="Number(semester)"
                      >
                        Semester {{ semester }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Enrolment/effective date *</label>
                    <input
                      v-model="form.enrolledAt"
                      type="date"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Source system *</label>
                    <input
                      v-model.trim="form.sourceSystem"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Source reference</label>
                    <input
                      v-model.trim="form.sourceReference"
                      class="form-control"
                    />
                  </div>
                  <div class="col-12">
                    <label class="form-label">Import reason *</label>
                    <textarea
                      v-model.trim="form.importReason"
                      class="form-control"
                      rows="2"
                    ></textarea>
                  </div>
                </div>
              </section>

              <section v-else-if="currentStep === 2">
                <h6 class="section-title">Optional personal information</h6>
                <div
                  v-if="locationLoadError"
                  class="alert alert-warning py-2 small"
                  role="alert"
                >
                  {{ locationLoadError }}
                </div>
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Date of birth</label>
                    <input
                      v-model="form.dateOfBirth"
                      type="date"
                      class="form-control"
                      :max="maxDateOfBirth"
                    />
                    <div class="form-text">
                      Student must be at least 15 years old.
                    </div>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Gender</label>
                    <select v-model="form.gender" class="form-select">
                      <option value="">Not provided</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Marital status</label>
                    <select v-model="form.maritalStatus" class="form-select">
                      <option value="">Not provided</option>
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="widowed">Widowed</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Nationality</label>
                    <select
                      v-model="form.nationality"
                      class="form-select"
                      :disabled="loadingLocations"
                      @change="onNationalityChange"
                    >
                      <option value="">Not provided</option>
                      <option
                        v-for="country in countries"
                        :key="country"
                        :value="country"
                      >
                        {{ country }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Place of birth</label>
                    <select
                      v-if="form.nationality === 'Nigeria'"
                      v-model="form.placeOfBirth"
                      class="form-select"
                      :disabled="loadingLocations || !states.length"
                    >
                      <option value="">Not provided</option>
                      <option
                        v-for="state in states"
                        :key="state"
                        :value="state"
                      >
                        {{ state }}
                      </option>
                    </select>
                    <input
                      v-else
                      v-model.trim="form.placeOfBirth"
                      class="form-control"
                      placeholder="City, state, or province"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">State of origin</label>
                    <select
                      v-if="form.nationality === 'Nigeria'"
                      v-model="form.stateOfOrigin"
                      class="form-select"
                      :disabled="loadingLocations || !states.length"
                      @change="fetchOriginLgas({ clearSelection: true })"
                    >
                      <option value="">Not provided</option>
                      <option
                        v-for="state in states"
                        :key="state"
                        :value="state"
                      >
                        {{ state }}
                      </option>
                    </select>
                    <input
                      v-else
                      v-model.trim="form.stateOfOrigin"
                      class="form-control"
                      placeholder="State or province"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">LGA</label>
                    <select
                      v-if="form.nationality === 'Nigeria'"
                      v-model="form.lga"
                      class="form-select"
                      :disabled="
                        loadingOriginLgas ||
                        !form.stateOfOrigin ||
                        !originLgas.length
                      "
                    >
                      <option value="">Not provided</option>
                      <option v-for="lga in originLgas" :key="lga" :value="lga">
                        {{ lga }}
                      </option>
                    </select>
                    <input
                      v-else
                      v-model.trim="form.lga"
                      class="form-control"
                      placeholder="Local government or district"
                    />
                  </div>
                  <div class="col-12">
                    <label class="form-label">Address line 1</label
                    ><input
                      v-model.trim="form.permanentAddress.line1"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Address line 2</label
                    ><input
                      v-model.trim="form.permanentAddress.line2"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">City / locality</label>
                    <select
                      v-if="form.permanentAddress.country === 'Nigeria'"
                      v-model="form.permanentAddress.city"
                      class="form-select"
                      :disabled="
                        loadingAddressCities ||
                        !form.permanentAddress.state ||
                        !addressCities.length
                      "
                    >
                      <option value="">Not provided</option>
                      <option
                        v-for="city in addressCities"
                        :key="city"
                        :value="city"
                      >
                        {{ city }}
                      </option>
                    </select>
                    <input
                      v-else
                      v-model.trim="form.permanentAddress.city"
                      class="form-control"
                      placeholder="City or locality"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Address state</label>
                    <select
                      v-if="form.permanentAddress.country === 'Nigeria'"
                      v-model="form.permanentAddress.state"
                      class="form-select"
                      :disabled="loadingLocations || !states.length"
                      @change="fetchAddressCities({ clearSelection: true })"
                    >
                      <option value="">Not provided</option>
                      <option
                        v-for="state in states"
                        :key="state"
                        :value="state"
                      >
                        {{ state }}
                      </option>
                    </select>
                    <input
                      v-else
                      v-model.trim="form.permanentAddress.state"
                      class="form-control"
                      placeholder="State or province"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Country</label>
                    <select
                      v-model="form.permanentAddress.country"
                      class="form-select"
                      :disabled="loadingLocations"
                      @change="onAddressCountryChange"
                    >
                      <option value="">Not provided</option>
                      <option
                        v-for="country in countries"
                        :key="country"
                        :value="country"
                      >
                        {{ country }}
                      </option>
                    </select>
                  </div>
                </div>
                <hr class="my-4" />
                <div class="row g-3">
                  <div class="col-md-6">
                    <h6>Guardian</h6>
                    <input
                      v-model.trim="form.guardian.name"
                      class="form-control mb-2"
                      placeholder="Name"
                    />
                    <select
                      v-model="form.guardian.relationship"
                      class="form-select mb-2"
                    >
                      <option value="">Relationship not provided</option>
                      <option
                        v-for="relationship in relationshipOptions"
                        :key="relationship"
                        :value="relationship"
                      >
                        {{ relationship }}
                      </option>
                    </select>
                    <input
                      v-model.trim="form.guardian.phone"
                      class="form-control mb-2"
                      placeholder="Phone"
                    />
                    <textarea
                      v-model.trim="form.guardian.address"
                      class="form-control"
                      rows="2"
                      placeholder="Address"
                    ></textarea>
                  </div>
                  <div class="col-md-6">
                    <h6>Next of kin</h6>
                    <input
                      v-model.trim="form.nextOfKin.name"
                      class="form-control mb-2"
                      placeholder="Name"
                    />
                    <select
                      v-model="form.nextOfKin.relationship"
                      class="form-select mb-2"
                    >
                      <option value="">Relationship not provided</option>
                      <option
                        v-for="relationship in relationshipOptions"
                        :key="relationship"
                        :value="relationship"
                      >
                        {{ relationship }}
                      </option>
                    </select>
                    <input
                      v-model.trim="form.nextOfKin.phone"
                      class="form-control mb-2"
                      placeholder="Phone"
                    />
                    <textarea
                      v-model.trim="form.nextOfKin.address"
                      class="form-control"
                      rows="2"
                      placeholder="Address"
                    ></textarea>
                  </div>
                </div>
              </section>

              <section v-else-if="currentStep === 3">
                <h6 class="section-title">
                  Optional academic history and results
                </h6>
                <div class="row g-3 mb-4">
                  <div
                    class="col-md-6"
                    v-for="(label, key) in {
                      primary: 'Primary school',
                      secondary: 'Secondary school',
                    }"
                    :key="key"
                  >
                    <div class="border rounded p-3 h-100">
                      <h6>{{ label }}</h6>
                      <input
                        v-model.trim="form.academicBackground[key].name"
                        class="form-control mb-2"
                        placeholder="School name"
                      />
                      <div class="row g-2">
                        <div class="col-6">
                          <input
                            v-model="form.academicBackground[key].startDate"
                            type="date"
                            class="form-control"
                            :min="
                              key === 'primary'
                                ? minPrimaryStartDate
                                : minSecondaryStartDate
                            "
                            :max="today"
                            :disabled="
                              key === 'primary'
                                ? !form.dateOfBirth
                                : !form.academicBackground.primary.endDate
                            "
                          />
                          <div class="form-text">
                            {{
                              key === "primary" && !form.dateOfBirth
                                ? "Select date of birth first"
                                : key === "secondary" &&
                                  !form.academicBackground.primary.endDate
                                ? "Select primary school end date first"
                                : "Start date"
                            }}
                          </div>
                        </div>
                        <div class="col-6">
                          <input
                            v-model="form.academicBackground[key].endDate"
                            type="date"
                            class="form-control"
                            :min="
                              key === 'primary'
                                ? minPrimaryEndDate
                                : minSecondaryEndDate
                            "
                            :max="today"
                            :disabled="!form.academicBackground[key].startDate"
                          />
                          <div class="form-text">End date</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="border rounded p-3 mb-4">
                  <h6>JAMB information</h6>
                  <div class="row g-3">
                    <div class="col-md-8">
                      <label class="form-label">Registration number</label
                      ><input
                        v-model.trim="form.jamb.registrationNumber"
                        class="form-control"
                      />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Score</label
                      ><input
                        v-model.number="form.jamb.score"
                        type="number"
                        min="0"
                        max="400"
                        class="form-control"
                      />
                    </div>
                  </div>
                </div>
                <div
                  class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
                >
                  <div>
                    <h6 class="mb-1">O-Level information</h6>
                    <small class="text-muted"
                      >Select Completed to enter one or two sittings. A matching
                      result PDF may be uploaded for each available
                      sitting.</small
                    >
                  </div>
                  <select
                    v-model="form.oLevelResultStatus"
                    class="form-select olevel-status"
                  >
                    <option value="awaiting_result">Awaiting result</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div
                  v-if="form.oLevelResultStatus === 'awaiting_result'"
                  class="alert alert-warning small"
                >
                  Sitting details and O-Level result uploads are unavailable
                  while the result is awaiting. They can be provided later.
                </div>
                <div
                  v-for="(exam, examIndex) in form.examinations"
                  :key="examIndex"
                  class="border rounded p-3 mb-3"
                >
                  <div class="d-flex justify-content-between mb-3">
                    <strong>Sitting {{ examIndex + 1 }}</strong>
                    <button
                      v-if="examIndex > 0"
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="removeExamination(examIndex)"
                    >
                      Remove sitting
                    </button>
                  </div>
                  <div class="row g-2 mb-3">
                    <div class="col-md-4">
                      <label class="form-label">Exam type</label>
                      <select
                        v-model="exam.examType"
                        class="form-select"
                        :disabled="form.oLevelResultStatus !== 'completed'"
                      >
                        <option value="" disabled>Select exam type</option>
                        <option
                          v-for="examType in examTypeOptions"
                          :key="examType"
                          :value="examType"
                        >
                          {{ examType }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Exam year</label>
                      <select
                        v-model.number="exam.examYear"
                        class="form-select"
                        :disabled="form.oLevelResultStatus !== 'completed'"
                      >
                        <option value="" disabled>Select exam year</option>
                        <option
                          v-for="examYear in examYears"
                          :key="examYear"
                          :value="examYear"
                        >
                          {{ examYear }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Exam number</label>
                      <input
                        v-model.trim="exam.examNumber"
                        class="form-control"
                        placeholder="Exam number"
                        :disabled="form.oLevelResultStatus !== 'completed'"
                      />
                    </div>
                  </div>
                  <div
                    v-for="(subject, subjectIndex) in exam.subjects"
                    :key="subjectIndex"
                    class="row g-2 mb-2"
                  >
                    <div class="col">
                      <label class="form-label">Subject</label>
                      <select
                        v-model="subject.name"
                        class="form-select"
                        :disabled="form.oLevelResultStatus !== 'completed'"
                      >
                        <option value="" disabled>Select subject</option>
                        <option
                          v-for="subjectOption in availableSubjects(
                            exam,
                            subjectIndex
                          )"
                          :key="subjectOption"
                          :value="subjectOption"
                        >
                          {{ subjectOption }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Grade</label>
                      <select
                        v-model="subject.grade"
                        class="form-select"
                        :disabled="form.oLevelResultStatus !== 'completed'"
                      >
                        <option value="" disabled>Select grade</option>
                        <option
                          v-for="grade in gradeOptions"
                          :key="grade"
                          :value="grade"
                        >
                          {{ grade }}
                        </option>
                      </select>
                    </div>
                    <div class="col-auto">
                      <button
                        type="button"
                        class="btn btn-outline-danger"
                        :disabled="
                          exam.subjects.length === 1 ||
                          form.oLevelResultStatus !== 'completed'
                        "
                        @click="removeSubject(exam, subjectIndex)"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="
                      exam.subjects.length >= 11 ||
                      form.oLevelResultStatus !== 'completed'
                    "
                    @click="addSubject(exam)"
                  >
                    Add subject
                  </button>
                </div>
                <button
                  v-if="form.examinations.length < 2"
                  type="button"
                  class="btn btn-outline-primary"
                  :disabled="form.oLevelResultStatus !== 'completed'"
                  @click="addExamination"
                >
                  Add second sitting
                </button>
              </section>

              <section v-else-if="currentStep === 4">
                <div
                  class="d-flex justify-content-between align-items-center mb-3"
                >
                  <h6 class="section-title mb-0">Optional private documents</h6>
                  <span class="badge text-bg-light border">{{
                    fileTotalLabel
                  }}</span>
                </div>
                <div class="alert alert-info small">
                  JPEG/PNG is accepted for the passport photograph. Other
                  documents must be PDF. Each file is limited to 2 MB and the
                  combined limit is 5 MB.
                </div>
                <div class="row g-3">
                  <div
                    v-for="document in importDocumentOptions"
                    :key="document.key"
                    class="col-md-6"
                  >
                    <div class="border rounded p-3 h-100">
                      <label class="form-label fw-semibold">{{
                        document.label
                      }}</label>
                      <input
                        :key="`${document.key}:${formKey}`"
                        type="file"
                        class="form-control"
                        :accept="document.accept"
                        @change="selectFile(document.key, $event)"
                      />
                      <div class="form-text text-truncate">
                        {{ fileName(document.key) }}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section v-else-if="currentStep === 5">
                <h6 class="section-title">Verified legacy payments</h6>
                <div class="alert alert-warning small">
                  Select only payments independently verified outside Paystack.
                  No gateway transaction will be fabricated.
                </div>
                <div v-if="previewing" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm"></div>
                </div>
                <template v-else>
                  <div
                    v-if="!preview?.applicableCharges?.length"
                    class="text-muted border rounded p-3"
                  >
                    No applicable configured charges were found.
                  </div>
                  <label
                    v-for="charge in preview?.applicableCharges || []"
                    :key="charge.selectionKey"
                    class="payment-option border rounded p-3 mb-2 d-flex gap-3"
                  >
                    <input
                      type="checkbox"
                      class="form-check-input mt-1"
                      :checked="
                        form.selectedPayments.includes(charge.selectionKey)
                      "
                      @change="togglePayment(charge.selectionKey)"
                    />
                    <span class="flex-grow-1">
                      <span class="d-flex justify-content-between gap-3"
                        ><strong>{{ charge.name }}</strong
                        ><strong>{{ money(charge.amount) }}</strong></span
                      >
                      <small class="text-muted d-block"
                        >{{ charge.sessionYear }} ·
                        {{
                          charge.ownerType === "application"
                            ? "Admission application"
                            : "Student account"
                        }}</small
                      >
                    </span>
                  </label>
                  <div v-if="form.selectedPayments.length" class="row g-3 mt-2">
                    <div class="col-md-6">
                      <label class="form-label"
                        >Payment recognition date *</label
                      ><input
                        v-model="form.paymentRecognitionDate"
                        type="date"
                        class="form-control"
                      />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Evidence/reference</label
                      ><input
                        v-model.trim="form.paymentEvidenceReference"
                        class="form-control"
                      />
                    </div>
                    <div class="col-12">
                      <label class="form-check border rounded p-3"
                        ><input
                          v-model="form.paymentVerificationConfirmed"
                          type="checkbox"
                          class="form-check-input ms-0 me-2"
                        /><span class="form-check-label"
                          >I confirm that the selected payments were verified
                          outside Paystack.</span
                        ></label
                      >
                    </div>
                  </div>
                </template>
              </section>

              <section v-else>
                <h6 class="section-title">Review and confirmation</h6>
                <div v-if="previewing" class="text-center py-5">
                  <div class="spinner-border text-primary"></div>
                </div>
                <template v-else-if="preview">
                  <div
                    v-if="preview.conflicts?.length"
                    class="alert alert-danger"
                  >
                    <strong>Resolve these issues before importing:</strong>
                    <ul class="mb-0 mt-2">
                      <li
                        v-for="conflict in preview.conflicts"
                        :key="conflict.field"
                      >
                        {{ conflict.message }}
                      </li>
                    </ul>
                  </div>
                  <div class="row g-3 mb-4">
                    <div
                      v-for="item in [
                        ['Student', preview.student?.fullName],
                        [
                          'Matriculation number',
                          preview.student?.matriculationNumber,
                        ],
                        ['Programme', preview.student?.programme],
                        ['Entry session', preview.student?.entrySession],
                        [
                          'Current placement',
                          `${preview.student?.currentSession || ''} · Year ${
                            preview.student?.year || ''
                          } · Semester ${preview.student?.semester || ''}`,
                        ],
                        [
                          'O-Level status',
                          preview.student?.oLevelResultStatus ===
                          'awaiting_result'
                            ? 'Awaiting result'
                            : 'Completed',
                        ],
                        ['Documents', `${Object.keys(files).length} selected`],
                        [
                          'Legacy payments',
                          `${form.selectedPayments.length} selected · ${money(
                            preview.selectedPaymentTotal
                          )}`,
                        ],
                      ]"
                      :key="item[0]"
                      class="col-md-6"
                    >
                      <div class="border rounded p-3 h-100">
                        <small class="text-muted d-block">{{ item[0] }}</small
                        ><strong>{{ item[1] || "Not provided" }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="alert alert-danger-subtle border-danger-subtle">
                    <label class="form-label fw-semibold"
                      >Enter the matriculation number to confirm *</label
                    >
                    <input
                      v-model.trim="form.confirmation"
                      class="form-control text-uppercase"
                      :placeholder="form.matriculationNumber"
                    />
                    <div
                      v-if="executeDisabledReason"
                      class="form-text text-danger mt-2"
                    >
                      {{ executeDisabledReason }}
                    </div>
                  </div>
                </template>
                <div v-else class="alert alert-warning">
                  <strong>Review could not be refreshed.</strong>
                  <p class="mb-3 mt-1">
                    {{
                      previewError ||
                      "The form has changed and must be validated again."
                    }}
                  </p>
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    :disabled="previewing"
                    @click="refreshPreview()"
                  >
                    Refresh review
                  </button>
                </div>
              </section>
            </div>
          </template>
        </div>

        <div class="modal-footer justify-content-between">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="currentStep === 0 || submitting"
            @click="previousStep"
          >
            Previous
          </button>
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-light"
              data-bs-dismiss="modal"
              :disabled="submitting"
            >
              Cancel
            </button>
            <button
              v-if="currentStep < steps.length - 1"
              type="button"
              class="btn btn-primary"
              :disabled="loadingOptions || previewing"
              @click="nextStep"
            >
              <span
                v-if="previewing"
                class="spinner-border spinner-border-sm me-1"
              ></span
              >Continue
            </button>
            <button
              v-else
              type="button"
              class="btn btn-success"
              :disabled="!canExecute || previewing"
              :title="executeDisabledReason"
              @click="executeImport"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm me-1"
              ></span
              >Create student
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.legacy-stepper {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
}
.legacy-step {
  min-width: 92px;
  border: 0;
  background: transparent;
  color: #6c757d;
  text-align: center;
}
.legacy-step span {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  margin: 0 auto 0.35rem;
  border: 1px solid #ced4da;
  border-radius: 50%;
  background: #fff;
  font-weight: 700;
}
.legacy-step small {
  white-space: nowrap;
}
.legacy-step.active {
  color: #0d6efd;
}
.legacy-step.active span {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}
.legacy-step.complete {
  color: #198754;
  cursor: pointer;
}
.legacy-step.complete span {
  background: #198754;
  border-color: #198754;
  color: #fff;
}
.section-title {
  font-weight: 700;
  margin-bottom: 1rem;
}
.olevel-status {
  max-width: 220px;
}
.payment-option {
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.payment-option:hover {
  border-color: #86b7fe !important;
}
@media (max-width: 767.98px) {
  .modal-dialog {
    margin: 0;
    min-height: 100%;
  }
  .modal-content {
    min-height: 100vh;
    border-radius: 0;
  }
}
</style>
